import React, { useState, useEffect } from 'react';
import { GoogleMap, Marker } from '@react-google-maps/api';

const containerStyle = {
  width: '100%',
  height: '250px',
  borderRadius: '8px',
};

const mapContainerStyle = {
  width: '100%',
  height: '100%',
  borderRadius: '8px',
};

function LocationView({ address }) {
  const [center, setCenter] = useState({ lat: 37.7749, lng: -122.4194 }); // Default to SF
  const [markerPosition, setMarkerPosition] = useState(null);
  const [streetViewEmbedUrl, setStreetViewEmbedUrl] = useState(null);

  useEffect(() => {
    if (!address || !window.google || !window.google.maps || !window.google.maps.Geocoder) {
      console.log('Google Maps API not loaded yet or no address provided');
      return;
    }

    console.log('Attempting to geocode address:', address);
    const geocoder = new window.google.maps.Geocoder();
    
    geocoder.geocode({ address: address }, (results, status) => {
      console.log('Geocoding results:', { results, status });
      
      if (status === 'OK' && results && results.length > 0) {
        const location = results[0].geometry.location;
        const coords = {
          lat: location.lat(),
          lng: location.lng(),
        };
        console.log('Successfully geocoded to coordinates:', coords);
        setCenter(coords);
        setMarkerPosition(coords);
        
        // Find the best Street View position and heading to face the building
        const findBestStreetView = async () => {
          try {
            // Use Google's Street View Service to find the nearest street view location
            const streetViewService = new window.google.maps.StreetViewService();
            
            streetViewService.getPanorama(
              { 
                location: coords, 
                radius: 50, // Reduced radius to stay closer to street
                preference: window.google.maps.StreetViewPreference.NEAREST,
                source: window.google.maps.StreetViewSource.OUTDOOR // Force outdoor only
              },
              (data, status) => {
                if (status === 'OK' && data && data.location) {
                  const streetViewLocation = data.location.latLng;
                  
                  // Additional check: make sure we're not too close (likely indoor)
                  const distance = window.google.maps.geometry.spherical.computeDistanceBetween(
                    streetViewLocation,
                    new window.google.maps.LatLng(coords.lat, coords.lng)
                  );
                  
                  // If we're too close (less than 10 meters), it's likely indoor - use fallback
                  if (distance < 10) {
                    console.log('Street View too close to building (likely indoor), using fallback');
                    useFallbackStreetView();
                    return;
                  }
                  
                  // Calculate the heading from street view position to the building
                  const heading = window.google.maps.geometry.spherical.computeHeading(
                    streetViewLocation,
                    new window.google.maps.LatLng(coords.lat, coords.lng)
                  );
                  
                  console.log('Street View location:', streetViewLocation.lat(), streetViewLocation.lng());
                  console.log('Building location:', coords.lat, coords.lng);
                  console.log('Distance to building:', distance, 'meters');
                  console.log('Calculated heading towards building:', heading);
                  
                  // Use the street view location and calculated heading
                  const embedUrl = `https://www.google.com/maps/embed/v1/streetview?key=AIzaSyBjMoiDAimpIhmRmg5A8kXlsWoDdeCHSgY&location=${streetViewLocation.lat()},${streetViewLocation.lng()}&heading=${Math.round(heading)}&pitch=0&fov=80&source=outdoor`;
                  setStreetViewEmbedUrl(embedUrl);
                } else {
                  console.log('Street View Service failed, using fallback');
                  useFallbackStreetView();
                }
              }
            );
          } catch (error) {
            console.error('Error in Street View calculation:', error);
            useFallbackStreetView();
          }
        };
        
        const useFallbackStreetView = () => {
          // Fallback: use original location with offset to ensure we're on the street
          let heading = 0;
          
          // Analyze address for better heading guess
          const addressLower = address.toLowerCase();
          if (addressLower.includes('north') || addressLower.includes(' n ')) {
            heading = 180; // Face south towards building
          } else if (addressLower.includes('south') || addressLower.includes(' s ')) {
            heading = 0; // Face north towards building  
          } else if (addressLower.includes('east') || addressLower.includes(' e ')) {
            heading = 270; // Face west towards building
          } else if (addressLower.includes('west') || addressLower.includes(' w ')) {
            heading = 90; // Face east towards building
          } else if (addressLower.includes('street') || addressLower.includes('st')) {
            heading = 90; // Default perpendicular to street
          }
          
          // Use original coordinates with smart heading
          const embedUrl = `https://www.google.com/maps/embed/v1/streetview?key=AIzaSyBjMoiDAimpIhmRmg5A8kXlsWoDdeCHSgY&location=${coords.lat},${coords.lng}&heading=${heading}&pitch=0&fov=80&source=outdoor`;
          setStreetViewEmbedUrl(embedUrl);
        };
        
        // Check if geometry library is available for heading calculations
        if (window.google.maps.geometry && window.google.maps.geometry.spherical) {
          findBestStreetView();
        } else {
          console.log('Geometry library not available, using simple heading');
          const embedUrl = `https://www.google.com/maps/embed/v1/streetview?key=AIzaSyBjMoiDAimpIhmRmg5A8kXlsWoDdeCHSgY&location=${coords.lat},${coords.lng}&heading=0&pitch=0&fov=80&source=outdoor`;
          setStreetViewEmbedUrl(embedUrl);
        }
      } else {
        console.error(`Geocoding failed. Status: ${status}, Address: ${address}`);
        if (results) {
          console.error('Results:', results);
        }
      }
    });
  }, [address]);

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px', height: '250px' }}>
      {/* Regular Map View */}
      <div style={{ borderRadius: '8px', overflow: 'hidden' }}>

        <div style={{ height: 'calc(100% - 28px)' }}>
          <GoogleMap
            mapContainerStyle={mapContainerStyle}
            center={center}
            zoom={16}
            options={{
              mapTypeId: 'roadmap',
              streetViewControl: false,
              fullscreenControl: false,
              mapTypeControl: false,
            }}
          >
            {markerPosition && <Marker position={markerPosition} />}
          </GoogleMap>
        </div>
      </div>

      {/* Satellite View */}
      <div style={{ borderRadius: '8px', overflow: 'hidden' }}>

        <div style={{ height: 'calc(100% - 28px)' }}>
                      <GoogleMap
              mapContainerStyle={mapContainerStyle}
              center={center}
              zoom={18}
              options={{
                mapTypeId: 'satellite',
                streetViewControl: false,
                fullscreenControl: false,
                mapTypeControl: false,
              }}
            >
            {markerPosition && <Marker position={markerPosition} />}
          </GoogleMap>
        </div>
      </div>

      {/* Street View */}
      <div style={{ borderRadius: '8px', overflow: 'hidden' }}>

        <div style={{ height: 'calc(100% - 28px)', backgroundColor: '#f3f4f6', borderRadius: '8px', overflow: 'hidden' }}>
          {streetViewEmbedUrl ? (
            <iframe
              src={streetViewEmbedUrl}
              width="100%"
              height="100%"
              style={{ border: 0, borderRadius: '8px' }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Street View"
            />
          ) : (
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center', 
              height: '100%',
              color: '#6b7280', 
              fontSize: '14px' 
            }}>
              Loading Street View...
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default LocationView; 